'use client';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import Link from "next/link";  // Importa il router

export default function SearchBoxHome({className}: {className?: string}) {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null); // Stato per l'errore

    const handleSearch = async () => {
        try {
            setError(null); // Resetta l'errore prima di fare la chiamata
            if (searchQuery.trim() !== '') {
                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/articoli/search?q=${encodeURIComponent(searchQuery.trim())}`);

                if (!response.ok) {
                    throw new Error('Errore nella ricerca, riprova più tardi.');
                }

                const data = await response.json();

                if (data.data.length === 0) {
                    setError('Nessun risultato trovato.');
                    setShowResults(true);
                } else {
                    setSearchResults(data.data);
                    setShowResults(true);
                }
            } else {
                setShowResults(false);
            }
        } catch (err: any) {
            setError(err.message || 'Errore nella ricerca, riprova più tardi.');
            setSearchResults([]);
            setShowResults(true);
        }
    };

    useEffect(() => {
        if (searchQuery.length < 2) {
            setShowResults(false);
        }

        if (searchQuery.trim() === '') {
            setShowResults(false);
        }

        if (searchQuery.trim() !== '') {
            handleSearch();
        }
    }, [searchQuery]);


    return (
        <div className="relative max-w-4xl mx-auto">
            <div className={`flex items-center rounded-full border border-black/20 bg-white px-1.5 py-3 shadow ${className}`}>
                <Input
                    className="flex-1 bg-transparent max-h-[50px] !border-0 text-base font-normal leading-tight focus:outline-none focus-visible:ring-offset-0 focus-visible:ring-0 !py-0"
                    placeholder="Quale auto cerchi?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery && setShowResults(true)}
                />

                <Button  className="" variant="link" aria-label="Cerca"
                        aria-labelledby="search-label" onClick={handleSearch}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.4791 9.90297C15.0161 8.34405 14.9516 6.64747 14.2975 5.13171C13.6434 3.61596 12.4447 2.38527 10.9266 1.67066C9.40844 0.956057 7.67517 0.806684 6.05214 1.25058C4.42912 1.69448 3.02796 2.70113 2.11169 4.08155C1.19541 5.46197 0.827043 7.12124 1.07572 8.74787C1.32441 10.3745 2.17304 11.8567 3.46231 12.9161C4.75159 13.9755 6.39285 14.5393 8.07799 14.5018C9.76313 14.4642 11.3763 13.8278 12.6146 12.712" stroke="#0131B8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12.6758 12.7801L17.0007 17" stroke="#0131B8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </Button>
                <span id="search-label" className="sr-only">Cerca</span>
            </div>


            {showResults && (
                <div
                    className="results-container absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-sm shadow-xl rounded-lg max-h-60 overflow-y-auto z-10 border border-gray-200"
                    style={{ width: "100%" }}
                    onMouseEnter={() => {
                        // Se hai una funzione per gestire l'hover del container
                        // handleResultsMouseEnter();
                    }}
                    onMouseLeave={() => {
                        // Se hai una funzione per gestire quando si esce dai risultati
                        // handleResultsMouseLeave();
                    }}
                >
                    {error ? (
                        <div className="p-6 text-center bg-white">
                            <div className="flex flex-col items-center space-y-3">
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-lg font-semibold text-gray-900 mb-1">Nessun risultato trovato</p>
                                    <p className="text-sm text-gray-600">
                                        Non abbiamo trovato veicoli per <span className="font-medium text-gray-900">"{searchQuery}"</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-2">Prova con termini più generici</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        searchResults.length > 0 ? (
                            <div className="py-2">
                                {searchResults.map((result, index) => (
                                    <Link
                                        key={index}
                                        className="group block px-4 py-3 cursor-pointer transition-all duration-200 ease-in-out hover:bg-blue-50 hover:border-l-4 hover:border-l-primary relative overflow-hidden"
                                        href={result.url}
                                        hrefLang="it"
                                    >
                                        {/* Effetto di background animato */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>

                                        {/* Contenuto principale */}
                                        <div className="relative z-10 flex items-center justify-between">
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors duration-200">
                                                    <span className="font-semibold">{result.marca}</span> {result.modello}
                                                </p>
                                                {/* Aggiungi informazioni extra se disponibili */}
                                                {result.anno && (
                                                    <p className="text-xs text-gray-500 mt-1 group-hover:text-gray-600 transition-colors duration-200">
                                                        Anno: {result.anno}
                                                    </p>
                                                )}
                                                {/*{result.prezzo && (
                                                    <p className="text-xs text-green-600 font-medium mt-1 group-hover:text-green-700 transition-colors duration-200">
                                                        €{result.prezzo.toLocaleString()}
                                                    </p>
                                                )}*/}
                                            </div>

                                            {/* Icona freccia */}
                                            <div className="ml-3 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-200">
                                                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Separatore sottile */}
                                        {index < searchResults.length - 1 && (
                                            <div className="absolute bottom-0 left-4 right-4 h-px bg-gray-100 group-hover:bg-transparent transition-colors duration-200"></div>
                                        )}
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 text-gray-500 text-sm text-center">
                                <div className="flex flex-col items-center space-y-2">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <p>Nessun risultato trovato.</p>
                                    <p className="text-xs">Prova con termini diversi</p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}
